import React, { useState, useRef } from 'react';
import { Camera, Loader } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { uploadProfilePicture } from '../../services/storage/profileStorage';

interface ProfilePictureProps {
  imageUrl: string | null;
  onUpdate: (url: string) => void;
}

export function ProfilePicture({ imageUrl, onUpdate }: ProfilePictureProps) {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user, isAuthenticated } = useAuth();
  const [avatarError, setAvatarError] = useState(false);

  const getDisplayName = () => {
    return user?.user_metadata?.first_name 
      ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`.trim()
      : 'User';
  };

  const getAvatarUrl = () => {
    if (avatarError || !imageUrl) {
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(getDisplayName())}&background=random`;
    }
    return imageUrl;
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    
    // Reset file input value
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setIsUploading(true);
    try {
      const url = await uploadProfilePicture(user.id, file);
      setAvatarError(false);
      onUpdate(url);
      // Force a refresh of the image by adding a timestamp
      const refreshedUrl = `${url}?t=${Date.now()}`;
      onUpdate(refreshedUrl);
      toast.success('Profile picture updated');
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      setAvatarError(true);
      toast.error('Failed to upload profile picture');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="relative group">
      <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-200">
        {isAuthenticated && (
          <>
            <img
              src={getAvatarUrl()}
              alt="Profile"
              className="w-full h-full object-cover bg-gray-100"
              onError={(e) => {
                setAvatarError(true);
                const target = e.target as HTMLImageElement;
                target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(getDisplayName())}&background=random`;
              }}
            />
            {(!imageUrl || avatarError) && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Camera className="h-12 w-12 text-gray-400" />
              </div>
            )}
          </>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={isUploading}
        className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 text-white opacity-0 group-hover:opacity-100 transition-opacity rounded-full"
      >
        {isUploading ? (
          <Loader className="h-6 w-6 animate-spin" />
        ) : (
          <Camera className="h-6 w-6" />
        )}
      </button>
    </div>
  );
}